/**
 * LookupCombobox — searchable dropdown with inline 「＋ 新規追加」.
 *
 * Solves the user complaint "資材 have to manual input everything and the
 * data is not connected across the apps" for the 3 shared free-text fields
 * that suffered from typo-duplicates:
 *
 *   • kind="makers"    → メーカー
 *   • kind="uoms"      → 単位
 *   • kind="materials" → 材質
 *
 * UX contract:
 *   1. Type to filter — case-insensitive on `name`
 *   2. Click a suggestion → onChange(name)  (stores the display name, not id,
 *      so historical records survive rename/soft-delete)
 *   3. When no exact match → shows「＋ 「<typed>」を追加」 at the bottom;
 *      click POSTs to /api/lookups/{kind}, refreshes local cache, then
 *      selects it. Toast confirms.
 *   4. Anywhere the parent needs to force-free-text (e.g. legacy import),
 *      pass `allowFreeText` — Escape / blur without picking commits the raw
 *      value.
 *
 * Also caches the fetched list at the module level per-kind so 20 lines in
 * one dialog only trigger 1 network call across all combos.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretDown, MagnifyingGlass, Plus, Check } from '@phosphor-icons/react';
import { useAuth, canEditLookups } from '@/auth/AuthContext';
import { Input } from './input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/lookups`;

// Per-kind in-memory cache: dozens of comboboxes in one form share ONE fetch
const _cache = { makers: null, uoms: null, materials: null };
const _pending = { makers: null, uoms: null, materials: null };

async function fetchLookup(kind) {
  if (_cache[kind]) return _cache[kind];
  if (_pending[kind]) return _pending[kind];
  _pending[kind] = axios.get(`${API}/${kind}`).then((r) => {
    _cache[kind] = r.data || [];
    _pending[kind] = null;
    return _cache[kind];
  }).catch(() => {
    _pending[kind] = null;
    return [];
  });
  return _pending[kind];
}

async function addLookup(kind, name) {
  const r = await axios.post(`${API}/${kind}`, { name });
  // Bust the cache so all mounted combos of this kind pick up the new entry
  _cache[kind] = null;
  return r.data;
}

const LABEL_JA = { makers: 'メーカー', uoms: '単位', materials: '材質' };

export function LookupCombobox({
  kind,
  value,
  onChange,
  placeholder,
  className = '',
  inputClassName = '',
  testId,
  disabled = false,
  allowFreeText = true,
}) {
  const { user } = useAuth();
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchLookup(kind).then((rows) => { if (!cancelled) setOptions(rows); });
    return () => { cancelled = true; };
  }, [kind]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = query.trim();
  const filtered = q
    ? options.filter((o) => {
        const s = q.toLowerCase();
        return (
          (o.name || '').toLowerCase().includes(s) ||
          (o.code || '').toLowerCase().includes(s) ||
          (o.yomi || '').toLowerCase().includes(s)
        );
      })
    : options;
  const hasExactMatch = filtered.some((o) => (o.name || '').toLowerCase() === q.toLowerCase());
  const canAddNew = q && !hasExactMatch && canEditLookups(user);

  const pick = useCallback((name) => {
    onChange(name);
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const commitFreeText = () => {
    if (!allowFreeText) return;
    if (q && q !== value) {
      onChange(q);
      setQuery('');
    }
    setOpen(false);
  };

  const addNew = useCallback(async () => {
    if (!q || saving) return;
    setSaving(true);
    try {
      const created = await addLookup(kind, q);
      const refreshed = await fetchLookup(kind);
      setOptions(refreshed);
      pick(created.name);
      toast.success(`「${created.name}」を${LABEL_JA[kind]}に追加しました`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${LABEL_JA[kind]}の追加に失敗しました`);
    } finally {
      setSaving(false);
    }
  }, [kind, q, saving, pick]);

  const displayValue = open ? query : (value || '');

  return (
    <div className={`relative ${className}`} ref={wrapRef} data-testid={testId ? `${testId}-wrap` : undefined}>
      <div className="relative">
        <MagnifyingGlass size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <Input
          data-testid={testId}
          value={displayValue}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setQuery(value || ''); setOpen(true); }}
          onBlur={() => {
            // Delay so mouseup on dropdown items still registers
            setTimeout(() => { if (open) commitFreeText(); }, 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); setQuery(''); }
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canAddNew) addNew();
              else if (filtered.length === 1) pick(filtered[0].name);
              else commitFreeText();
            }
          }}
          placeholder={placeholder || `${LABEL_JA[kind]}を検索...`}
          className={`h-9 pl-7 pr-6 text-xs ${inputClassName}`}
          disabled={disabled || saving}
        />
        <CaretDown size={11} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
      </div>
      {open && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-56 overflow-y-auto"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {filtered.length === 0 && !canAddNew && (
            <div className="px-3 py-2 text-[11px] text-zinc-400">候補がありません</div>
          )}
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(o.name); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 flex items-center gap-2 ${
                (value || '').toLowerCase() === (o.name || '').toLowerCase() ? 'bg-emerald-50/40 font-semibold' : ''
              }`}
              data-testid={testId ? `${testId}-opt-${o.id}` : undefined}
            >
              {(value || '').toLowerCase() === (o.name || '').toLowerCase() && (
                <Check size={11} weight="bold" className="text-emerald-600 shrink-0" />
              )}
              {o.code ? (
                <span className="inline-flex items-center px-1 py-0.5 rounded bg-zinc-100 text-zinc-600 font-mono text-[10px] shrink-0">
                  {o.code}
                </span>
              ) : null}
              <span className="truncate">{o.name}</span>
            </button>
          ))}
          {canAddNew && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addNew(); }}
              disabled={saving}
              className="w-full text-left px-3 py-1.5 text-xs border-t border-zinc-100 bg-rose-50/50 hover:bg-rose-100 text-rose-700 flex items-center gap-1.5 font-medium"
              data-testid={testId ? `${testId}-add-new` : undefined}
            >
              <Plus size={11} weight="bold" />
              <span>「{q}」を{LABEL_JA[kind]}に追加</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default LookupCombobox;
